import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLaboratories } from '../../../hooks/useLaboratories';
import { useDeleteLaboratory } from './useDeleteLaboratory';

const toMessageText = (data, fallback) => (typeof data === 'string' && data ? data : fallback);

// Orchestrates the laboratory register list, shared by the web and mobile versions.
export function useLaboratoryListScreen() {
  const navigate = useNavigate();

  const { laboratories, isLoading, error: listError, refetch } = useLaboratories();
  const { remove, isDeleting } = useDeleteLaboratory();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLaboratory, setSelectedLaboratory] = useState(null);
  const [laboratoryToEdit, setLaboratoryToEdit] = useState(null);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(toMessageText(listError, 'Erro ao carregar laboratórios.'));
    }
  }, [listError]);

  const clickMenu = useCallback((event, laboratory) => {
    setAnchorEl(event.currentTarget);
    setSelectedLaboratory(laboratory);
  }, []);

  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const openCreateModal = useCallback(() => {
    setLaboratoryToEdit(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback(() => {
    setLaboratoryToEdit(selectedLaboratory);
    setModalOpen(true);
    closeMenu();
  }, [selectedLaboratory, closeMenu]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setLaboratoryToEdit(null);
  }, []);

  const handleSaved = useCallback(
    (message) => {
      refetch();
      setSuccessMessage(toMessageText(message, 'Laboratório salvo com sucesso.'));
      setShowSuccessMessage(true);
    },
    [refetch],
  );

  const openConfirmModal = useCallback(() => {
    closeMenu();
    setConfirmModalOpen(true);
  }, [closeMenu]);

  const closeConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
    setSelectedLaboratory(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!selectedLaboratory) return;

    setConfirmModalOpen(false);
    remove(selectedLaboratory.id)
      .then((returnFromApi) => {
        refetch();
        setSuccessMessage(toMessageText(returnFromApi.data, 'Laboratório excluído com sucesso.'));
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(
          toMessageText(returnFromApi.response?.data, 'Erro ao excluir laboratório.'),
        );
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setSelectedLaboratory(null));
  }, [selectedLaboratory, remove, refetch, navigate]);

  return {
    laboratories,
    isLoading: isLoading || isDeleting,

    anchorEl,
    clickMenu,
    closeMenu,

    isModalOpen,
    laboratoryToEdit,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSaved,

    isConfirmModalOpen,
    openConfirmModal,
    closeConfirmModal,
    confirmDelete,

    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  };
}
